/**
 * Shifts hooks (TanStack Query)
 *
 * A shift is a nurse's work period; patients attach to it as assignments. These
 * hooks end a shift, list the caller's shifts, read a shift's patient
 * assignments, and assign / end an individual patient.
 *
 * Assign/end rewrite ACL grants, so they are server-authoritative
 * (NOT optimistic): showing a provisional assignment before the server confirms
 * could mislead a nurse about who holds patient access — same rule as clinical
 * documents. Each mutation invalidates the shift + assignment caches on settle.
 */

import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import {
  listShifts,
  getShift,
  getWardAllocation,
  assignPatient,
  endAssignment,
  endShift,
  type ListShiftsParams,
  type ShiftResponse,
  type AssignmentResponse,
  type AssignPatientRequest,
  type WardAllocationResponse,
} from '@/api/endpoints/shifts';
import { errorToast } from '@/hooks/errorToast';
import { useCurrentUser } from '@/hooks/useAuth';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';

export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  list: (params: ListShiftsParams) => [...shiftKeys.lists(), params] as const,
  detail: (id: string) => [...shiftKeys.all, 'detail', id] as const,
  wardAllocation: (locationId: string) => [...shiftKeys.all, 'ward-allocation', locationId] as const,
};

/**
 * List the caller's shifts (the backend force-scopes to the caller). `active`
 * restricts to the open work period.
 */
export function useShifts(
  params: ListShiftsParams = {},
  options: { enabled?: boolean } = {},
) {
  return useAuthenticatedQuery<ShiftResponse[]>({
    queryKey: shiftKeys.list(params),
    queryFn: () => listShifts(params),
    enabled: options.enabled,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
}

/** Read a single shift with its active patient assignments. */
export function useShift(
  id: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useAuthenticatedQuery<ShiftResponse>({
    queryKey: shiftKeys.detail(id ?? ''),
    queryFn: () => getShift(id as string),
    enabled: (options.enabled ?? true) && !!id,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
}

/**
 * The caller's current work period plus its patient assignments — the
 * "my patients this shift" surface. Composes two queries (list active shifts →
 * read the open one's assignments) and presents a single set of async-state
 * flags so callers don't juggle both.
 *
 * patientIds are the assignment patient ids newest-first (stable order for
 * chips/preselect). assignments carries the full rows for actions (handover,
 * end). openShift is undefined when the nurse is off duty.
 */
export function useActiveShiftAssignments(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const user = useCurrentUser();
  const shiftsQuery = useShifts({ active: true }, { enabled });
  const openShift = useMemo(() => {
    if (!shiftsQuery.data || shiftsQuery.data.length === 0) return undefined;
    if (user?.id) {
      const match = shiftsQuery.data.find(
        (s) => (s.user ?? s.nurse_id) === user.id,
      );
      if (match) return match;
    }
    return shiftsQuery.data[0];
  }, [shiftsQuery.data, user?.id]);
  const detailQuery = useShift(openShift?.id, { enabled: enabled && !!openShift });

  const assignments = useMemo<AssignmentResponse[]>(
    () => detailQuery.data?.assignments ?? [],
    [detailQuery.data],
  );
  const patientIds = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const a of assignments) {
      if (seen.has(a.patient_id)) continue;
      seen.add(a.patient_id);
      ordered.push(a.patient_id);
    }
    return ordered;
  }, [assignments]);

  // The shift list resolves first; only wait on the detail query when there is
  // an open shift to read. A disabled query never sets isError, so the plain
  // OR is exact.
  const isLoading =
    shiftsQuery.isLoading || (!!openShift && detailQuery.isLoading);
  const isError = shiftsQuery.isError || detailQuery.isError;
  const isFetching = shiftsQuery.isFetching || detailQuery.isFetching;

  return {
    openShift,
    assignments,
    patientIds,
    isLoading,
    isError,
    isFetching,
    refetch: () => {
      void shiftsQuery.refetch();
      void detailQuery.refetch();
    },
  };
}

function invalidateShifts(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: shiftKeys.all });
}

/** Assign a patient into the caller's own shift (cold). */
export function useAssignPatient() {
  const queryClient = useQueryClient();
  return useMutation<
    AssignmentResponse,
    Error,
    { shiftId: string; body: AssignPatientRequest }
  >({
    mutationFn: ({ shiftId, body }) => assignPatient(shiftId, body),
    onError: () => {
      errorToast({
        context: 'assignPatient',
        description: i18n.t('shifts.assign.error', {
          defaultValue: 'Could not assign the patient.',
        }),
      });
    },
    onSettled: () => invalidateShifts(queryClient),
  });
}

/** End one patient's custody within a shift. */
export function useEndAssignment() {
  const queryClient = useQueryClient();
  return useMutation<
    AssignmentResponse,
    Error,
    { shiftId: string; assignmentId: string }
  >({
    mutationFn: ({ shiftId, assignmentId }) =>
      endAssignment(shiftId, assignmentId),
    onError: () => {
      errorToast({
        context: 'endAssignment',
        description: i18n.t('shifts.endAssignment.error', {
          defaultValue: 'Could not end the assignment.',
        }),
      });
    },
    onSettled: () => invalidateShifts(queryClient),
  });
}

/** Read the ward's allocation frame (the Schichtstart board). */
export function useWardAllocation(
  locationId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useAuthenticatedQuery<WardAllocationResponse>({
    queryKey: shiftKeys.wardAllocation(locationId ?? ''),
    queryFn: () => getWardAllocation(locationId as string),
    enabled: (options.enabled ?? true) && !!locationId,
    retry: 1,
    retryDelay: 1000,
    refetchOnWindowFocus: true,
  });
}

/** End the whole shift. */
export function useEndShift() {
  const queryClient = useQueryClient();
  return useMutation<ShiftResponse, Error, string>({
    mutationFn: (shiftId) => endShift(shiftId),
    onError: () => {
      errorToast({
        context: 'endShift',
        description: i18n.t('shifts.end.error', {
          defaultValue: 'Could not end the shift.',
        }),
      });
    },
    onSettled: () => invalidateShifts(queryClient),
  });
}
